/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Device} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {useComplianceDashboardQuery} from '@ion/query/src/hooks/useComplianceDashboardQuery';
import useEmissionTester from '@ion/query/src/hooks/useEmissionTester';
import useGetAssetByDeviceId from '@ion/query/src/hooks/useGetAssetByDeviceId';
import {CarbTabGridInitalState} from './helpers';
import {CarbColumnType, CarbTabGridDataType} from './types';

interface UseCarbDetalsProperies {
  currentDevice: Device;
};

/**
 * The useCarbDetails hook is used to retrieve the `emission Tester` and `assets`
 * data associated with current device.
 * 
 * ```typescript
 * 
 *    import useCarbDetails from './useCarbDetails';
 * 
 *    const {carbRowData, isLoading} = useCarbDetails({currentDevice});
 * 
 * ```
 */
export const useCarbDetails = ({currentDevice}: UseCarbDetalsProperies) => {
  const {id: deviceId, esn, account}                 = currentDevice || {};
  const currentDeviceAccountId                       = entityId(account?.href);
  const {result: assetDetail, isLoading}             = useGetAssetByDeviceId({deviceId}, !!deviceId);
  const {emissionTester, isLoading: isTesterLoading} = useEmissionTester({id: currentDeviceAccountId}, true);
  const {data: carbResult, isLoading: isCarbLoading} = useComplianceDashboardQuery({esn});

  const [carbRowData, setCarbRowData] = useState<CarbTabGridDataType[]>(
    CarbTabGridInitalState(currentDevice, assetDetail[0], emissionTester)
  );

  useEffect(() => {
    const {vin, licenseNumber, country, licenseState} = assetDetail[0] || {};
    const {submissionStatus, submissionDate}          = carbResult?.carb?.[0] || {};
    const {emailAddress}                              = emissionTester || {};

    if (!isLoading && !isTesterLoading) {
      setCarbRowData([
        {
          [CarbColumnType.ESN]               : esn || '',
          [CarbColumnType.VIN]               : vin || '',
          [CarbColumnType.LICENSE_PLATE]     : licenseNumber || '',
          [CarbColumnType.LAST_SUBMISSION]   : submissionDate ? formatDateTime(`${submissionDate}Z`) : '',
          [CarbColumnType.EMAIL_ADDRESS]     : emailAddress || '',
          [CarbColumnType.COUNTRY]           : country || '',
          [CarbColumnType.STATE]             : licenseState || '',
          [CarbColumnType.SUBMISSION_STATUS] : submissionStatus || ''
        }
      ]);
    }
  }, [isLoading, isTesterLoading, isCarbLoading, assetDetail]);

  return {carbRowData, isLoading: (isLoading || isTesterLoading || isCarbLoading)};
};

export default useCarbDetails;
